import type {
  AdminApiPlaybook,
  PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { CriteriaGradingRecord } from "@repo/app-types/entities/recording";
import { checkIsApplicableGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { type FC, useMemo } from "react";
import {
  AssessmentCriteriaNode,
  AssessmentCriteriaNodeCommentary,
  AssessmentCriteriaNodeGrading,
  AssessmentCriteriaNodeTitle,
} from "../../components/assessment/assessmentCriteriaNode.js";
import {
  AssessmentSectionNode,
  AssessmentSectionNodeContent,
  AssessmentSectionNodeTitle,
} from "../../components/assessment/assessmentSectionNode.js";
import { AssessmentTable } from "../../components/assessment/assessmentTable.js";
import { renderGradingValue } from "../teammatePerformance/gradingHelpers.js";

export interface ChapterAssessmentTableProps {
  playbook: AdminApiPlaybook;
  criteriaGradings: CriteriaGradingRecord;
}

export const ChapterAssessmentTable: FC<ChapterAssessmentTableProps> = (
  props,
) => {
  const { playbook, criteriaGradings } = props;

  const defaultExpandedIds = useMemo(() => {
    if (!playbook) {
      return [];
    }

    return collectSectionIdsWithApplicableCriteria(
      playbook.assessment_tree.nodes,
      criteriaGradings,
    );
  }, [playbook, criteriaGradings]);

  const renderNode = (node: PlaybookNode) => {
    switch (node.type) {
      case "section": {
        return (
          <AssessmentSectionNode
            key={node.id}
            id={node.id}
            textValue={node.name}
          >
            <AssessmentSectionNodeContent>
              <AssessmentSectionNodeTitle>
                {node.name}
              </AssessmentSectionNodeTitle>
            </AssessmentSectionNodeContent>
            {renderNodes(node.nodes)}
          </AssessmentSectionNode>
        );
      }

      case "criteria": {
        return (
          <AssessmentCriteriaNode
            key={node.id}
            textValue={node.name}
            onPress={undefined}
          >
            <AssessmentCriteriaNodeTitle className="self-start font-medium whitespace-normal">
              {node.name}
            </AssessmentCriteriaNodeTitle>
            <div>
              <AssessmentCriteriaNodeGrading>
                {renderGradingValue(
                  node.grading_value_type,
                  criteriaGradings[node.id]?.value || "n/a",
                )}
              </AssessmentCriteriaNodeGrading>
              <AssessmentCriteriaNodeCommentary>
                {criteriaGradings[node.id]?.commentary}
              </AssessmentCriteriaNodeCommentary>
            </div>
          </AssessmentCriteriaNode>
        );
      }

      default: {
        assertUnreachable(node);
      }
    }
  };

  const renderNodes = (nodes: readonly PlaybookNode[]) => {
    return nodes.map(renderNode);
  };

  return (
    <AssessmentTable
      className="grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
      defaultExpandedIds={defaultExpandedIds}
    >
      {renderNodes(playbook.assessment_tree.nodes)}
    </AssessmentTable>
  );
};

function collectSectionIdsWithApplicableCriteria(
  nodes: readonly PlaybookNode[],
  criteriaGradings: CriteriaGradingRecord,
): string[] {
  const sectionIdsWithApplicableCriteria = new Set<string>();

  const traverse = (
    sectionId: string | null,
    children: readonly PlaybookNode[],
  ) => {
    for (const node of children) {
      if (
        node.type === "criteria" &&
        checkIsApplicableGradingValue(criteriaGradings[node.id]?.value)
      ) {
        if (sectionId) {
          sectionIdsWithApplicableCriteria.add(sectionId);
        }
      } else if (node.type === "section") {
        traverse(node.id, node.nodes);
      }
    }
  };

  traverse(null, nodes);

  return Array.from(sectionIdsWithApplicableCriteria);
}
